import React, { useState } from 'react';
import { Box, Stack, Typography, Button, Divider } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { FlightBadge, PreferenceBadge, TeeSheetLegend } from './TeeSheetBadges';
import { effectiveRoundBool } from './Functions/applyUniformStartHoleToViewEventData';
import {
    buildDynamicShotgunHoleSlots,
    resolveRoundHoles,
    filterRegsForShotgunSlot,
    type ShotgunHoleSlot,
} from './Functions/shotgunTeeSheetUtils';
import { isOpsActiveRegistration } from '@moment-golf/shared';

// ─── Palette constants (mirrors appTheme.tsx) ────────────────────────────────

const PRIMARY = {
    main: 'rgba(30, 78, 55, 1)',
    extraLight: 'rgba(101, 170, 136, 0.50)',
} as const;

const SEPIA = {
    bg: 'rgb(253, 252, 248)',
    extraLight: 'rgb(229, 215, 191)',
    light: 'rgb(189, 148, 98)',
    main: 'rgba(143, 92, 43, 1)',
} as const;

const CREAM = 'rgba(247, 242, 221, 1)';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeeSheetDisplayProps {
    registrations: any[];
    teeTimes: string[];
    roundValue: number;
    viewEvent: any;
    courseData?: any[];
    factor?: number;
}

type ViewMode = 'teeTime' | 'alphabetical';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
    });
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TeeSheetDisplay = ({
    registrations,
    teeTimes,
    roundValue,
    viewEvent,
    courseData = [],
    factor = 1,
}: TeeSheetDisplayProps) => {
    const [viewMode, setViewMode] = useState<ViewMode>('teeTime');

    const isShotgunRound = Boolean(
        viewEvent && effectiveRoundBool(viewEvent.rounds?.[roundValue - 1]?.shotgunStart),
    );

    const activeRegs = registrations.filter(
        (reg: any) => isOpsActiveRegistration(reg),
    );

    // For shotgun rounds, prefer the tee time from scores (teeTimes prop).
    // Fall back to registration data so the grid view shows the correct time
    // even when scores haven't been generated yet.
    const shotgunTeeTimeIso: string = isShotgunRound
        ? (teeTimes.length > 0
            ? teeTimes[0]
            : activeRegs
                .find((reg: any) =>
                    (reg.teeTimes ?? []).some(
                        (tt: any) => tt.round === roundValue && tt.teeTime,
                    ),
                )
                ?.teeTimes?.find((tt: any) => tt.round === roundValue)?.teeTime ?? '')
        : '';

    const holeSlots: ShotgunHoleSlot[] = isShotgunRound
        ? buildDynamicShotgunHoleSlots(
            resolveRoundHoles(viewEvent, { courseData }, roundValue),
            activeRegs,
            roundValue,
        )
        : [];

    const getRegTeeTime = (reg: any): string =>
        reg.teeTimes?.[roundValue - 1]?.teeTime ?? '';

    const getRegHole = (reg: any): string =>
        reg.teeTimes?.[roundValue - 1]?.startingHole != null
            ? String(reg.teeTimes[roundValue - 1].startingHole)
            : '—';

    const getDivisionIndex = (reg: any): number =>
        Math.max(
            0,
            viewEvent?.flighting?.divisions?.findIndex(
                (d: any) => d.divisionName === reg.division,
            ) ?? 0,
        );

    // ─── PDF Download ─────────────────────────────────────────────────────────
    //
    // PDF_STYLES mirrors the display palette above. Update here when the
    // on-screen design changes so both stay in sync.
    //
    const PDF_STYLES = {
        // Column header row (primary.main)
        headerFill:    [30, 78, 55]   as [number, number, number],
        headerText:    [247, 242, 221] as [number, number, number],
        // Alternate row tint (sepia.bg) — only color contrast used in body
        altFill:       [245, 242, 236] as [number, number, number],
        // Cell borders (sepia.extraLight)
        borderColor:   [210, 195, 173] as [number, number, number],
        // Primary body text
        bodyText:      [30, 30, 30]   as [number, number, number],
        // Secondary / label text (sepia.main, used for document subtitle)
        secondaryText: [100, 70, 35]  as [number, number, number],
        font:          'helvetica'    as const,
        fontSize:      9,
        cellPadding:   4,
    } as const;

    // Build the badge string shown inside each player slot: "A·1"
    const badgeCode = (reg: any): string => {
        if (!reg.division) return '';
        return `${reg.division.charAt(0).toUpperCase()}${reg.flight ? `·${reg.flight.charAt(0).toUpperCase()}` : ''}`;
    };

    // Build the legend lines from all active registrations (division / flight only)
    const buildLegend = (): string => {
        const parts: string[] = [];
        const seenDiv = new Set<string>();
        activeRegs.forEach((reg: any) => {
            if (!reg.division) return;
            const key = `${reg.division}|${reg.flight ?? ''}`;
            if (seenDiv.has(key)) return;
            seenDiv.add(key);
            const letter = `${reg.division.charAt(0).toUpperCase()}${reg.flight ? `·${reg.flight.charAt(0).toUpperCase()}` : ''}`;
            const label = reg.division + (reg.flight ? ` · ${reg.flight}` : '');
            parts.push(`${letter} = ${label}`);
        });
        return parts.length ? `Legend:  ${parts.join('   ')}` : '';
    };

    const downloadTeeSheetPDF = () => {
        const slotsPerGroup: number = viewEvent?.regPerTeeTime ?? 4;
        const eventName: string = viewEvent?.eventName ?? 'Event';
        const timestamp = new Date().toUTCString();
        const safeName = eventName.replace(/[^a-z0-9]/gi, '_');

        // Portrait letter: usable width = 612 - 80 = 532pt
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
        const MARGIN = 40;
        const PAGE_W = 612;
        const USABLE_W = PAGE_W - MARGIN * 2;

        // ── shared page header ─────────────────────────────────────────────────
        const writeDocHeader = (startY: number): number => {
            doc.setFontSize(15);
            doc.setTextColor(...PDF_STYLES.headerFill);
            doc.text(`${eventName} — Round ${roundValue} Tee Sheet`, MARGIN, startY);
            doc.setFontSize(8);
            doc.setTextColor(...PDF_STYLES.secondaryText);
            doc.text(`Downloaded ${timestamp}`, MARGIN, startY + 13);
            return startY + 26;
        };

        // ── helper: text content for one player slot ───────────────────────────
        interface SlotData { name: string; team: string; badge: string; }

        const slotText = (s: SlotData | null): string => {
            if (!s) return '';
            const lines: string[] = [s.name];
            if (s.team) lines.push(s.team);
            if (s.badge) lines.push(s.badge);
            return lines.join('\n');
        };

        // ── legend writer (shared between both pages) ──────────────────────────
        const writeLegend = (afterY: number) => {
            const legend = buildLegend();
            if (!legend) return;
            doc.setFontSize(7.5);
            doc.setTextColor(...PDF_STYLES.secondaryText);
            doc.text(legend, MARGIN, afterY + 10);
        };

        // ═══════════════════════════════════════════════════════════════════════
        // PAGE 1 — Tee Time view (portrait, one row per group, N equal player cols)
        // ═══════════════════════════════════════════════════════════════════════
        let startY = writeDocHeader(44);

        // Column widths: fixed for Tee Time + Hole, equal split for player slots
        const ttTimeW = 58;
        const ttHoleW = 58;
        const ttPlayerW = (USABLE_W - ttTimeW - ttHoleW) / slotsPerGroup;

        type ColStyle = { cellWidth: number; fontStyle?: 'normal' | 'bold' | 'italic' | 'bolditalic' };
        const ttColumnStyles: Record<string, ColStyle> = {
            0: { cellWidth: ttTimeW, fontStyle: 'bold' },
            1: { cellWidth: ttHoleW, fontStyle: 'bold' },
        };
        for (let i = 0; i < slotsPerGroup; i++) {
            ttColumnStyles[i + 2] = { cellWidth: ttPlayerW };
        }

        const slotHeaders = Array.from({ length: slotsPerGroup }, (_, i) => `Player ${i + 1}`);
        const ttBody: string[][] = [];

        if (isShotgunRound) {
            holeSlots.forEach((slot) => {
                const group = filterRegsForShotgunSlot(
                    activeRegs,
                    roundValue,
                    slot.holeNumber,
                    slot.shotgunOrder,
                ).sort((a: any, b: any) => {
                    if (a.scoreCode < b.scoreCode) return -1;
                    if (a.scoreCode > b.scoreCode) return 1;
                    return (a.playerName ?? '').localeCompare(b.playerName ?? '');
                });
                if (group.length === 0) return;

                const slots = Array.from({ length: slotsPerGroup }, (_, i) => {
                    const reg = group[i] ?? null;
                    if (!reg) return null;
                    return { name: reg.playerName ?? '', team: reg.teamName ?? '', badge: badgeCode(reg) };
                }).map(s => slotText(s));
                ttBody.push([
                    slot.label,
                    formatTime(shotgunTeeTimeIso),
                    ...slots,
                ]);
            });
        } else {
            teeTimes.forEach((teeTime) => {
                const group = activeRegs
                    .filter((reg: any) => getRegTeeTime(reg) === teeTime)
                    .sort((a: any, b: any) => {
                        if (a.scoreCode < b.scoreCode) return -1;
                        if (a.scoreCode > b.scoreCode) return 1;
                        return (a.playerName ?? '').localeCompare(b.playerName ?? '');
                    });
                if (group.length === 0) return;

                const slots = Array.from({ length: slotsPerGroup }, (_, i) => {
                    const reg = group[i] ?? null;
                    if (!reg) return null;
                    return { name: reg.playerName ?? '', team: reg.teamName ?? '', badge: badgeCode(reg) };
                }).map(s => slotText(s));
                ttBody.push([formatTime(teeTime), getRegHole(group[0]), ...slots]);
            });
        }

        autoTable(doc, {
            startY,
            margin: { left: MARGIN, right: MARGIN },
            head: [[
                isShotgunRound ? 'Start Hole' : 'Tee Time',
                isShotgunRound ? 'Tee Time' : 'Hole',
                ...slotHeaders,
            ]],
            body: ttBody,
            columnStyles: ttColumnStyles,
            headStyles: {
                fillColor: PDF_STYLES.headerFill,
                textColor: PDF_STYLES.headerText,
                fontStyle: 'bold',
                fontSize: PDF_STYLES.fontSize,
            },
            alternateRowStyles: { fillColor: PDF_STYLES.altFill },
            styles: {
                font: PDF_STYLES.font,
                fontSize: PDF_STYLES.fontSize,
                cellPadding: PDF_STYLES.cellPadding,
                textColor: PDF_STYLES.bodyText,
                lineColor: PDF_STYLES.borderColor,
                lineWidth: 0.5,
                valign: 'top',
            },
        });

        writeLegend((doc as any).lastAutoTable.finalY);

        // ═══════════════════════════════════════════════════════════════════════
        // PAGE 2 — Alphabetical view (one row per player)
        // ═══════════════════════════════════════════════════════════════════════
        doc.addPage();
        startY = writeDocHeader(44);

        const sorted = [...activeRegs].sort((a: any, b: any) =>
            (a.playerName ?? '').localeCompare(b.playerName ?? ''),
        );

        const hasTeams = activeRegs.some((reg: any) => reg.teamName);
        const hasDiv = activeRegs.some((reg: any) => reg.division || reg.flight);

        const alphaBody = sorted.map((reg: any) => {
            const row: string[] = [reg.playerName ?? ''];
            if (hasTeams)   row.push(reg.teamName ?? '');
            row.push(formatTime(getRegTeeTime(reg)), getRegHole(reg));
            if (hasDiv) row.push(badgeCode(reg));
            return row;
        });

        const alphaHeaders: string[] = ['Player'];
        if (hasTeams)   alphaHeaders.push('Team');
        alphaHeaders.push('Tee Time', 'Hole');
        if (hasDiv) alphaHeaders.push('Div');

        // Fixed widths for narrow columns; name columns split the remaining space
        const alphaTimeW  = 58;
        const alphaHoleW  = 28;
        const alphaDivW   = hasDiv ? 58 : 0;
        const nameColsCount = hasTeams ? 2 : 1;
        const alphaNameW  = (USABLE_W - alphaTimeW - alphaHoleW - alphaDivW) / nameColsCount;

        // Build columnStyles dynamically so indices always match alphaHeaders
        let ci = 0;
        const alphaColumnStyles: Record<string, ColStyle> = {};
        alphaColumnStyles[ci++] = { cellWidth: alphaNameW };                       // Player
        if (hasTeams)   alphaColumnStyles[ci++] = { cellWidth: alphaNameW };       // Team
        alphaColumnStyles[ci++] = { cellWidth: alphaTimeW, fontStyle: 'bold' };    // Tee Time
        alphaColumnStyles[ci++] = { cellWidth: alphaHoleW, fontStyle: 'bold' };    // Hole
        if (hasDiv) alphaColumnStyles[ci++] = { cellWidth: alphaDivW };            // Div

        autoTable(doc, {
            startY,
            margin: { left: MARGIN, right: MARGIN },
            head: [alphaHeaders],
            body: alphaBody,
            columnStyles: alphaColumnStyles,
            headStyles: {
                fillColor: PDF_STYLES.headerFill,
                textColor: PDF_STYLES.headerText,
                fontStyle: 'bold',
                fontSize: PDF_STYLES.fontSize,
            },
            alternateRowStyles: { fillColor: PDF_STYLES.altFill },
            styles: {
                font: PDF_STYLES.font,
                fontSize: PDF_STYLES.fontSize,
                cellPadding: PDF_STYLES.cellPadding,
                textColor: PDF_STYLES.bodyText,
                lineColor: PDF_STYLES.borderColor,
                lineWidth: 0.5,
            },
        });

        writeLegend((doc as any).lastAutoTable.finalY);

        doc.save(`${safeName}_Round${roundValue}_TeeSheet.pdf`);
    };

    // ─── XLSX Export ──────────────────────────────────────────────────────────
    //
    // Two sheets mirroring the PDF layout: group view (page 1) + alphabetical
    // (page 2). Player cells contain name only; team/division appear as
    // separate columns in the alphabetical sheet.
    //
    const downloadTeeSheetXLSX = () => {
        const slotsPerGroup = viewEvent?.regPerTeeTime ?? 4;
        const eventName: string = viewEvent?.eventName ?? 'Event';
        const safeName = eventName.replace(/[^a-z0-9]/gi, '_');

        const slotHeaders = Array.from({ length: slotsPerGroup }, (_, i) => `Player ${i + 1}`);

        // ── Sheet 1: group view (by start hole / by tee time) ─────────────────
        const ttHeaders = [
            isShotgunRound ? 'Start Hole' : 'Tee Time',
            isShotgunRound ? 'Tee Time'   : 'Hole',
            ...slotHeaders,
        ];
        const ttRows: string[][] = [ttHeaders];

        if (isShotgunRound) {
            holeSlots.forEach((slot) => {
                const group = filterRegsForShotgunSlot(
                    activeRegs,
                    roundValue,
                    slot.holeNumber,
                    slot.shotgunOrder,
                ).sort((a: any, b: any) => {
                    if (a.scoreCode < b.scoreCode) return -1;
                    if (a.scoreCode > b.scoreCode) return 1;
                    return (a.playerName ?? '').localeCompare(b.playerName ?? '');
                });
                if (group.length === 0) return;
                const slots = Array.from({ length: slotsPerGroup }, (_, i) =>
                    group[i]?.playerName ?? '',
                );
                ttRows.push([slot.label, formatTime(shotgunTeeTimeIso), ...slots]);
            });
        } else {
            teeTimes.forEach((teeTime) => {
                const group = activeRegs
                    .filter((reg: any) => getRegTeeTime(reg) === teeTime)
                    .sort((a: any, b: any) => {
                        if (a.scoreCode < b.scoreCode) return -1;
                        if (a.scoreCode > b.scoreCode) return 1;
                        return (a.playerName ?? '').localeCompare(b.playerName ?? '');
                    });
                if (group.length === 0) return;
                const slots = Array.from({ length: slotsPerGroup }, (_, i) =>
                    group[i]?.playerName ?? '',
                );
                ttRows.push([formatTime(teeTime), getRegHole(group[0]), ...slots]);
            });
        }

        const legendText = buildLegend();
        if (legendText) { ttRows.push([], [legendText]); }

        // ── Sheet 2: alphabetical ─────────────────────────────────────────────
        const sorted = [...activeRegs].sort((a: any, b: any) =>
            (a.playerName ?? '').localeCompare(b.playerName ?? ''),
        );
        const hasTeams = activeRegs.some((reg: any) => reg.teamName);
        const hasDiv = activeRegs.some((reg: any) => reg.division || reg.flight);

        const alphaHeaders: string[] = ['Player'];
        if (hasTeams)   alphaHeaders.push('Team');
        alphaHeaders.push('Tee Time', 'Hole');
        if (hasDiv) alphaHeaders.push('Div');

        const alphaRows: string[][] = [alphaHeaders];
        sorted.forEach((reg: any) => {
            const row: string[] = [reg.playerName ?? ''];
            if (hasTeams)   row.push(reg.teamName ?? '');
            row.push(formatTime(getRegTeeTime(reg)), getRegHole(reg));
            if (hasDiv) row.push(badgeCode(reg));
            alphaRows.push(row);
        });
        if (legendText) { alphaRows.push([], [legendText]); }

        // ── Assemble workbook ─────────────────────────────────────────────────
        const wb = XLSX.utils.book_new();
        const sheet1Name = isShotgunRound ? 'By Start Hole' : 'By Tee Time';

        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ttRows),    sheet1Name);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(alphaRows), 'Alphabetical');
        XLSX.writeFile(wb, `${safeName}_Round${roundValue}_TeeSheet.xlsx`);
    };

    // ─── Tee Time View ────────────────────────────────────────────────────────

    const renderPlayerSlots = (group: any[], slotsPerGroup: number) =>
        Array.from({ length: slotsPerGroup }, (_, slotIdx) => {
            const reg = group[slotIdx] ?? null;
            return reg ? (
                <Box
                    key={reg._id}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        px: 1,
                        py: 0.5,
                        borderLeft: slotIdx > 0 ? `1px solid ${SEPIA.extraLight}` : 'none',
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        spacing={0.5}
                        useFlexGap
                    >
                        <Typography
                            sx={{
                                fontSize: 13 * factor,
                                fontWeight: 700,
                                color: PRIMARY.main,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {reg.playerName}
                        </Typography>
                        {(reg.division || reg.flight || reg.teeGroupFocusPreference) && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{ flexShrink: 0 }}
                            >
                                <FlightBadge
                                    division={reg.division}
                                    flight={reg.flight}
                                    divisionIndex={getDivisionIndex(reg)}
                                    factor={factor}
                                />
                                <PreferenceBadge
                                    preference={reg.teeGroupFocusPreference}
                                    factor={factor}
                                />
                            </Stack>
                        )}
                    </Stack>
                    {reg.teamName && (
                        <Typography
                            sx={{
                                fontSize: 11 * factor,
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                mt: 0.25,
                            }}
                        >
                            {reg.teamName}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box
                    key={`empty-${slotIdx}`}
                    sx={{
                        borderLeft: slotIdx > 0 ? `1px solid ${SEPIA.extraLight}` : 'none',
                    }}
                />
            );
        });

    const renderTeeTimeView = () => {
        const slotsPerGroup: number = viewEvent?.regPerTeeTime ?? 4;
        return (
        <>
            {/* Column header — outside scroll so it stays visible */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{ borderBottom: `1.5px solid ${SEPIA.light}` }}
            >
                <Box sx={{ width: 96, flexShrink: 0, px: 1, py: 0.75, textAlign: 'center', borderRight: `1px solid ${SEPIA.extraLight}` }}>
                    <Typography sx={{ fontSize: 11 * factor, fontWeight: 600, color: 'text.secondary' }}>
                        Tee Time
                    </Typography>
                </Box>
                <Box sx={{ width: 96, flexShrink: 0, px: 1, py: 0.75, textAlign: 'center', borderRight: `1px solid ${SEPIA.extraLight}` }}>
                    <Typography sx={{ fontSize: 11 * factor, fontWeight: 600, color: 'text.secondary' }}>
                        Hole
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 1.5, py: 0.75 }}>
                    <Typography sx={{ fontSize: 11 * factor, fontWeight: 600, color: 'text.secondary' }}>
                        Players
                    </Typography>
                </Box>
                {teeTimes.length > 10 && (
                    <Box sx={{ width: '8px' }} />
                )}
            </Stack>

            {/* Scrollable data rows only */}
            <Box sx={{ overflowX: 'auto', width: '100%' }}>
            {teeTimes.map((teeTime, rowIndex) => {
                const group = activeRegs
                    .filter((reg: any) => getRegTeeTime(reg) === teeTime)
                    .sort((a: any, b: any) => {
                        if (a.scoreCode < b.scoreCode) return -1;
                        if (a.scoreCode > b.scoreCode) return 1;
                        return (a.playerName ?? '').localeCompare(b.playerName ?? '');
                    });
                if (group.length === 0) return null;

                const hole = getRegHole(group[0]);
                const isEven = rowIndex % 2 === 0;

                return (
                    <Stack
                        key={teeTime}
                        direction="row"
                        alignItems="center"
                        sx={{
                            backgroundColor: isEven ? SEPIA.bg : '#ffffff',
                            borderBottom: `1px solid ${SEPIA.extraLight}`,
                            minWidth: 420,
                            minHeight: 56,
                        }}
                    >
                        {/* Tee time cell — inherits row background, no extra fill */}
                        <Box
                            sx={{
                                width: 96,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                py: 1,
                                borderRight: `1px solid ${SEPIA.extraLight}`,
                                alignSelf: 'stretch',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 13 * factor,
                                    fontWeight: 700,
                                    color: PRIMARY.main,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatTime(teeTime)}
                            </Typography>
                        </Box>

                        {/* Hole cell */}
                        <Box
                            sx={{
                                width: 96,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                py: 1,
                                borderRight: `1px solid ${SEPIA.extraLight}`,
                                alignSelf: 'stretch',
                            }}
                        >
                            <Typography
                                sx={{ fontSize: 13 * factor, fontWeight: 600, color: 'text.secondary' }}
                            >
                                {hole}
                            </Typography>
                        </Box>

                        {/* Players area — no card outlines, equal-width slots */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns: `repeat(${slotsPerGroup}, 1fr)`,
                                px: 1,
                                py: 0.75,
                                gap: 0,
                            }}
                        >
                            {renderPlayerSlots(group, slotsPerGroup)}
                        </Box>
                    </Stack>
                );
            })}
            </Box>
        </>
        );
    };

    // ─── Shotgun View ─────────────────────────────────────────────────────────

    const renderShotgunView = () => {
        const slotsPerGroup: number = viewEvent?.regPerTeeTime ?? 4;
        return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                sx={{ borderBottom: `1.5px solid ${SEPIA.light}` }}
            >
                <Box sx={{ width: 96, flexShrink: 0, px: 1, py: 0.75, textAlign: 'center', borderRight: `1px solid ${SEPIA.extraLight}` }}>
                    <Typography sx={{ fontSize: 11 * factor, fontWeight: 600, color: 'text.secondary' }}>
                        Start Hole
                    </Typography>
                </Box>
                <Box sx={{ width: 96, flexShrink: 0, px: 1, py: 0.75, textAlign: 'center', borderRight: `1px solid ${SEPIA.extraLight}` }}>
                    <Typography sx={{ fontSize: 11 * factor, fontWeight: 600, color: 'text.secondary' }}>
                        Tee Time
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 1.5, py: 0.75 }}>
                    <Typography sx={{ fontSize: 11 * factor, fontWeight: 600, color: 'text.secondary' }}>
                        Players
                    </Typography>
                </Box>
                {holeSlots.length > 10 && (
                    <Box sx={{ width: '8px' }} />
                )}
            </Stack>

            <Box sx={{ overflowX: 'auto', width: '100%' }}>
            {holeSlots.map((slot, rowIndex) => {
                const group = filterRegsForShotgunSlot(
                    activeRegs,
                    roundValue,
                    slot.holeNumber,
                    slot.shotgunOrder,
                ).sort((a: any, b: any) => {
                    if (a.scoreCode < b.scoreCode) return -1;
                    if (a.scoreCode > b.scoreCode) return 1;
                    return (a.playerName ?? '').localeCompare(b.playerName ?? '');
                });
                if (group.length === 0) return null;

                const isEven = rowIndex % 2 === 0;

                return (
                    <Stack
                        key={`${slot.holeNumber}-${slot.shotgunOrder ?? 'main'}`}
                        direction="row"
                        alignItems="center"
                        sx={{
                            backgroundColor: isEven ? SEPIA.bg : '#ffffff',
                            borderBottom: `1px solid ${SEPIA.extraLight}`,
                            minWidth: 420,
                            minHeight: 56,
                        }}
                    >
                        <Box
                            sx={{
                                width: 96,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                py: 1,
                                borderRight: `1px solid ${SEPIA.extraLight}`,
                                alignSelf: 'stretch',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 13 * factor,
                                    fontWeight: 700,
                                    color: PRIMARY.main,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {slot.label}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                width: 96,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                py: 1,
                                borderRight: `1px solid ${SEPIA.extraLight}`,
                                alignSelf: 'stretch',
                            }}
                        >
                            <Typography
                                sx={{ fontSize: 13 * factor, fontWeight: 600, color: 'text.secondary' }}
                            >
                                {shotgunTeeTimeIso ? formatTime(shotgunTeeTimeIso) : '—'}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns: `repeat(${slotsPerGroup}, 1fr)`,
                                px: 1,
                                py: 0.75,
                                gap: 0,
                            }}
                        >
                            {renderPlayerSlots(group, slotsPerGroup)}
                        </Box>
                    </Stack>
                );
            })}
            </Box>
        </>
        );
    };

    // ─── Alphabetical View ────────────────────────────────────────────────────

    const renderAlphabeticalView = () => {
        const sorted = [...activeRegs].sort((a: any, b: any) =>
            (a.playerName ?? '').localeCompare(b.playerName ?? ''),
        );

        const colHeaders = [
            { label: 'Player',           flex: 3,   center: false, px: 1.5 },
            { label: 'Team',             flex: 2,   center: false, px: 1.5 },
            { label: 'Tee Time',         flex: 1.5, center: true,  px: 1.5 },
            { label: 'Hole',             flex: 0.8, center: true,  px: 1   },
            { label: 'Division / Flight',flex: 1.5, center: true,  px: 1   },
            { label: 'Preference',       flex: 1.2, center: true,  px: 1   },
        ];

        return (
            <>
                {/* Column header — outside scroll so it stays visible */}
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ borderBottom: `1.5px solid ${SEPIA.light}` }}
                >
                    {colHeaders.map((col, i) => (
                        <React.Fragment key={col.label}>
                            {i > 0 && (
                                <Divider orientation="vertical" flexItem sx={{ borderColor: SEPIA.extraLight }} />
                            )}
                            <Box sx={{ flex: col.flex, px: col.px, py: 0.75, flexShrink: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: 11 * factor,
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textAlign: col.center ? 'center' : 'left',
                                    }}
                                >
                                    {col.label}
                                </Typography>
                            </Box>
                        </React.Fragment>
                    ))}
                    {sorted.length > 10 && (
                         <Box sx={{ width: '8px'}} />
                    )}
                </Stack>

                {/* Scrollable data rows only */}
                <Box sx={{ overflowX: 'auto', width: '100%' }}>
                {sorted.map((reg: any, rowIndex: number) => {
                    const teeTimeIso = getRegTeeTime(reg);
                    const hole = getRegHole(reg);
                    const isEven = rowIndex % 2 === 0;

                    return (
                        <Stack
                            key={reg._id}
                            direction="row"
                            alignItems="center"
                            sx={{
                                backgroundColor: isEven ? SEPIA.bg : '#ffffff',
                                borderBottom: `1px solid ${SEPIA.extraLight}`,
                                minHeight: 48,
                                minWidth: 520,
                            }}
                        >
                            {/* Player */}
                            <Box sx={{ flex: 3, px: 1.5, py: 0.75, flexShrink: 0 }}>
                                <Typography
                                    sx={{ fontSize: 13 * factor, fontWeight: 700, color: PRIMARY.main }}
                                >
                                    {reg.playerName}
                                </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: SEPIA.extraLight }} />

                            {/* Team */}
                            <Box sx={{ flex: 2, px: 1.5, py: 0.75, flexShrink: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: 12 * factor,
                                        fontStyle: reg.teamName ? 'italic' : 'normal',
                                        color: SEPIA.main,
                                    }}
                                >
                                    {reg.teamName || '—'}
                                </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: SEPIA.extraLight }} />

                            {/* Tee Time */}
                            <Box
                                sx={{
                                    flex: 1.5,
                                    px: 1.5,
                                    py: 0.75,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 13 * factor,
                                        fontWeight: 700,
                                        color: PRIMARY.main,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {teeTimeIso ? formatTime(teeTimeIso) : '—'}
                                </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: SEPIA.extraLight }} />

                            {/* Hole */}
                            <Box
                                sx={{
                                    flex: 0.8,
                                    px: 1,
                                    py: 0.75,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Typography
                                    sx={{ fontSize: 13 * factor, fontWeight: 600, color: SEPIA.main }}
                                >
                                    {hole}
                                </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: SEPIA.extraLight }} />

                            {/* Division / Flight */}
                            <Box
                                sx={{
                                    flex: 1.5,
                                    px: 1,
                                    py: 0.75,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <FlightBadge
                                    division={reg.division}
                                    flight={reg.flight}
                                    divisionIndex={getDivisionIndex(reg)}
                                    factor={factor}
                                />
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: SEPIA.extraLight }} />

                            {/* Preference */}
                            <Box
                                sx={{
                                    flex: 1.2,
                                    px: 1,
                                    py: 0.75,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <PreferenceBadge
                                    preference={reg.teeGroupFocusPreference}
                                    factor={factor}
                                />
                            </Box>
                        </Stack>
                    );
                })}
                </Box>
            </>
        );
    };

    // ─── Root render ──────────────────────────────────────────────────────────

    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
                maxWidth: 1200,
                border: `1.5px solid ${SEPIA.light}`,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: SEPIA.bg,
                boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
                pb: 1,
            }}
        >
            {/* Header bar */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                sx={{
                    backgroundColor: SEPIA.bg,
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    borderBottom: `1px solid ${SEPIA.light}`,
                }}
            >
                <Typography
                    sx={{
                        fontSize: 16 * factor,
                        fontWeight: 700,
                        color: PRIMARY.main,
                        letterSpacing: '0.04em',
                        flexShrink: 0,
                    }}
                >
                    Tee Sheet
                </Typography>

                {/* View mode toggle */}
                <Stack direction="row" spacing={0.75} alignItems="center">
                    {(['teeTime', 'alphabetical'] as ViewMode[]).map((mode) => (
                        <Button
                            key={mode}
                            size="small"
                            onClick={() => setViewMode(mode)}
                            sx={{
                                fontSize: 12 * factor,
                                fontWeight: 600,
                                textTransform: 'none',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1.5,
                                border: `1.5px solid ${PRIMARY.main}`,
                                color: viewMode === mode ? CREAM : PRIMARY.main,
                                backgroundColor: viewMode === mode ? PRIMARY.main : 'transparent',
                                '&:hover': {
                                    backgroundColor:
                                        viewMode === mode
                                            ? PRIMARY.main
                                            : 'rgba(30, 78, 55, 0.08)',
                                    borderColor: PRIMARY.main,
                                },
                            }}
                        >
                            {mode === 'teeTime'
                                ? (isShotgunRound ? 'By Start Hole' : 'By Tee Time')
                                : 'Alphabetical'}
                        </Button>
                    ))}
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                {/* PDF download */}
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                        <DownloadIcon sx={{ fontSize: `${14 * factor}px !important` }} />
                    }
                    onClick={downloadTeeSheetPDF}
                    sx={{
                        fontSize: 12 * factor,
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        flexShrink: 0,
                    }}
                >
                    Download PDF
                </Button>

                {/* XLSX export */}
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                        <DownloadIcon sx={{ fontSize: `${14 * factor}px !important` }} />
                    }
                    onClick={downloadTeeSheetXLSX}
                    sx={{
                        fontSize: 12 * factor,
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        flexShrink: 0,
                    }}
                >
                    Export XLSX
                </Button>
                </Stack>
            </Stack>

            {/* Table body */}
            {viewMode === 'teeTime'
                ? (isShotgunRound ? renderShotgunView() : renderTeeTimeView())
                : renderAlphabeticalView()}

            {/* Legend */}
            <TeeSheetLegend
                registrations={registrations}
                flighting={viewEvent?.flighting}
                factor={factor}
            />
        </Stack>
    );
};
